import React, { useState, useEffect } from "react";
import { useUser } from "../../components/UserContext";
import { useApiClient } from "../../components/useApiClient";
import { useNavigate } from "react-router";

import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    List,
    ListItem,
    ListItemText,
    Divider,
    Typography,
    Box,
    Radio,
    Chip,
    Stack,
    Alert,
    FormControlLabel, Switch,
} from "@mui/material";

import SubscriptionSummaryCard from "../../components/LicenseManagement/SubscriptionSummaryCard";

type UserProfileDialogProps = {
    open: boolean;
    onClose: () => void;
};

function formatDate(value?: Date | null | undefined): string {
    if (!value) return "—";

    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "—";

    return d.toLocaleDateString("en-GB", {
        year: "numeric",
        month: "short",
        day: "2-digit",
    });
}

const UserProfileDialog: React.FC<UserProfileDialogProps> = ({ open, onClose }) => {
    const { user, getAccessToken, refreshUser } = useUser();
    const { put } = useApiClient();
    const navigate = useNavigate();

    const [displayName, setDisplayName] = useState("");
    const [canPerformValuations, setCanPerformValuations] = useState<boolean>(false);
    const [defaultClientId, setDefaultClientId] = useState<number | null>(null);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);

    // Sync local state when dialog opens or user changes
    useEffect(() => {
        if (!user) return;
        setDisplayName(user.displayName || "");
        setCanPerformValuations(user.canValueArtworks);
        setDefaultClientId(user.defaultClientId ?? null);
        setSaveError(null);
    }, [user, open]);

    const hasClients = (user?.clients?.length ?? 0) > 0;

    // Optional: if user has no default set, pick first client for UI convenience (doesn't persist until Save)
    useEffect(() => {
        if (!user) return;
        if (!hasClients) return;
        if (defaultClientId != null) return;

        const firstClientId = user.clients?.[0]?.clientId;
        if (typeof firstClientId === "number") setDefaultClientId(firstClientId);
    }, [user, hasClients, defaultClientId]);

    // Close the dialog before leaving for the subscription page (or any other in-app link).
    const handleNavigate = (path: string) => {
        onClose();
        navigate(path);
    };

    const handleSwitch = (event: React.ChangeEvent<HTMLInputElement>) => {
        setCanPerformValuations(event.target.checked);
    };

    const handleSave = async () => {
        if (!user) return;

        setSaving(true);
        setSaveError(null);
        try {
            const accessToken = await getAccessToken();
            if (!accessToken) return;

            const formData = new FormData();
            formData.append("Id", user.userId.toString());
            formData.append("Email", user.email); // keeping email as immutable here
            formData.append("DisplayName", displayName);
            formData.append("CanValueArtworks", canPerformValuations.toString());

            if (defaultClientId != null) {
                formData.append("DefaultClientId", defaultClientId.toString());
            }

            await put(`user/profile/${user.userId}`, accessToken, formData);

            refreshUser();
            onClose();
        } catch (e: any) {
            const body = e?.response?.data;
            setSaveError(
                (typeof body === "string" && body) || body?.message || e?.message || "Failed to update profile."
            );
        } finally {
            setSaving(false);
        }
    };

    if (!user) return null;

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>User Profile</DialogTitle>

            <DialogContent dividers>
                {/* Basic details */}
                <Box mb={2}>
                    <Typography variant="subtitle2" gutterBottom>
                        Account Details
                    </Typography>

                    <Box display="flex" flexDirection="column" gap={2}>
                        <TextField
                            label="Display name"
                            value={displayName}
                            onChange={(e) => setDisplayName(e.target.value)}
                            fullWidth
                        />

                        <TextField
                            label="Email"
                            type="email"
                            value={user.email ?? ""}
                            fullWidth
                            disabled
                        />
                    </Box>

                    <FormControlLabel
                        control={
                            <Switch
                                checked={canPerformValuations}
                                onChange={handleSwitch}
                            />
                        }
                        label="Can Perform Valuations"
                    />
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Terms / EULA */}
                <Box mb={2}>
                    <Typography variant="subtitle2" gutterBottom>
                        Terms / EULA
                    </Typography>

                    {user.hasAcceptedTerms ? (
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Chip size="small" label="Accepted" color="success" variant="outlined" />

                            <Typography variant="body2" color="text.secondary">
                                Version {user.termVersionAccepted ?? "—"} • Accepted{" "}
                                {formatDate(user.dateTermsAccepted)}
                            </Typography>
                        </Stack>
                    ) : (
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Chip size="small" label="Not accepted" color="warning" variant="outlined" />
                            <Typography variant="body2" color="text.secondary">
                                You have not accepted the current terms.
                            </Typography>
                        </Stack>
                    )}
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Subscription for the user's OWN tenant. Renders nothing for users who aren't a license holder. */}
                <SubscriptionSummaryCard open={open} onNavigate={handleNavigate} />
                <Divider sx={{ my: 2 }} />


                {/* Attached clients + default toggle */}
                <Box>
                    <Typography variant="subtitle2" gutterBottom>
                        Attached Clients / Collections
                    </Typography>

                    {!hasClients ? (
                        <Typography variant="body2" color="text.secondary">
                            You are not currently attached to any clients or collections.
                        </Typography>
                    ) : (
                        <>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                Select your default client (used as the starting context after sign-in).
                            </Typography>

                            <List dense>
                                {user.clients!.map((client) => {
                                    const selected = client.clientId === defaultClientId;

                                    return (
                                        <ListItem
                                            key={client.clientId}
                                            disableGutters
                                            secondaryAction={
                                                <Radio
                                                    edge="end"
                                                    checked={selected}
                                                    onChange={() => setDefaultClientId(client.clientId)}
                                                    inputProps={{ "aria-label": `Set ${client.clientName} as default` }}
                                                />
                                            }
                                        >
                                            <ListItemText
                                                primary={
                                                    <Stack direction="row" spacing={1} alignItems="center">
                                                        <Typography variant="body2">{client.clientName}</Typography>
                                                        {selected && <Chip size="small" label="Default" variant="outlined" />}
                                                    </Stack>
                                                }
                                                secondary={`(${client.roleName})`}
                                            />
                                        </ListItem>
                                    );
                                })}
                            </List>
                        </>
                    )}
                </Box>
            </DialogContent>

            {saveError && (
                <Box px={3} pt={1}>
                    <Alert severity="error" onClose={() => setSaveError(null)}>{saveError}</Alert>
                </Box>
            )}

            <DialogActions>
                <Button onClick={onClose} color="inherit" disabled={saving}>
                    Cancel
                </Button>
                <Button onClick={handleSave} variant="contained" disabled={saving}>
                    {saving ? "Saving..." : "Save changes"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default UserProfileDialog;